import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { useAuth } from '../hooks/useAuth.js';
import { ThanksForm, ThanksItem } from '../components/shared/ThanksCard.jsx';
import AiAssist from '../components/shared/AiAssist.jsx';

// Clubtrots: volunteer of the month, hall of fame and pride moments. Everyone in the
// club reads it; the "Clubtrots" permission chooses and posts.

const ERRORS = {
  invalid_month: 'Kies deze of een eerdere maand.', invalid_member: 'Kies een actief lid.', reason_required: 'Schrijf kort waarom.',
  invalid_coins: 'Coins: een heel getal van 0 tot 1000.', coins_disabled: 'Coins staan uit in deze club; laat de bonus op 0.',
  month_taken: 'Voor deze maand is al iemand gekozen.', title_required: 'Geef het moment een titel.', invalid_date: 'De datum klopt niet.',
  photo_type: 'De foto moet een afbeelding zijn.', photo_too_large: 'De foto is te groot (max. 10 MB).', invalid_body: 'De tekst is te lang.',
  forbidden: 'Dat mag je niet.', not_found: 'Dit bestaat niet (meer).',
  coins_admin_only: 'Alleen een clubbeheerder kan coins toekennen.', coins_self: 'Je kunt jezelf geen coins geven.',
  coins_already_given: 'Voor deze maand zijn al coins toegekend.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';

const monthLabel = m => new Date(`${m}-15T12:00:00`).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
const dayLabel = d => new Date(`${d}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const n = (count, one, many) => (count ? `${count} ${count === 1 ? one : many}` : null);
const recentMonths = (month, n) => Array.from({ length: n }, (_, i) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 - i, 15));
  return d.toISOString().slice(0, 7);
});

export function Avatar({ name, url, size = 56 }) {
  const initials = (name || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
  return url
    ? <img src={url} alt="" style={`width:${size}px;height:${size}px;border-radius:50%;object-fit:cover;flex-shrink:0`} />
    : <div aria-hidden="true" style={`width:${size}px;height:${size}px;border-radius:50%;background:var(--color-primary);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${Math.round(size / 2.6)}px;flex-shrink:0`}>{initials}</div>;
}

function ChooseForm({ month, taken, canGiveCoins, onSaved, onCancel }) {
  const months = recentMonths(month, 3).filter(m => !taken.has(m));
  const [form, setForm] = useState({ month: months[0] || month, user_id: '', reason: '', coins: 0 });
  const [suggestions, setSuggestions] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => { api('/api/pride/members').then(setMembers).catch(() => {}); }, []);
  useEffect(() => { setSuggestions(null); api(`/api/pride/suggestions?month=${form.month}`).then(setSuggestions).catch(() => setSuggestions([])); }, [form.month]);
  async function save() {
    setSaving(true); setError(null);
    try { onSaved(await api('/api/pride/volunteers', { method: 'POST', body: JSON.stringify({ ...form, coins: Number(form.coins) || 0 }) })); }
    catch (err) { setError(message(err)); }
    setSaving(false);
  }
  if (!months.length) return <div class="text-sm text-muted">Voor de afgelopen maanden is al iemand gekozen.</div>;
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <label class="form-label">Maand
        <select class="form-input" value={form.month} onChange={e => set('month', e.target.value)}>
          {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
      </label>
      <div>
        <div class="form-label">Suggesties — wie deed veel deze maand</div>
        {!suggestions && <div class="text-sm text-muted">Laden…</div>}
        {suggestions?.length === 0 && <div class="text-sm text-muted">Geen suggesties uit taken, klussen of storingen; kies hieronder zelf iemand.</div>}
        <div style="display:flex;flex-direction:column;gap:4px">
          {suggestions?.map(s => (
            <label key={s.id} style={`display:flex;gap:8px;align-items:center;padding:6px 8px;border:1px solid ${String(form.user_id) === String(s.id) ? 'var(--color-primary)' : 'var(--color-border)'};border-radius:8px;cursor:pointer;font-weight:400`}>
              <input type="radio" name="suggestion" checked={String(form.user_id) === String(s.id)} onChange={() => set('user_id', s.id)} />
              <span style="flex:1;min-width:0">
                <strong>{s.display_name}</strong>
                <span class="text-sm text-muted"> · {[n(s.tasks, 'taak', 'taken'), n(s.jaarplanning, 'jaarplanningsactiviteit', 'jaarplanningsactiviteiten'), n(s.klussen, 'klus', 'klussen'), n(s.storingen, 'storing gemeld', 'storingen gemeld'), n(s.bedankjes, 'bedankje', 'bedankjes')].filter(Boolean).join(', ')}{s.last_chosen ? ` · eerder gekozen (${monthLabel(s.last_chosen)})` : ''}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <label class="form-label">Of kies iemand anders
        <select class="form-input" value={form.user_id} onChange={e => set('user_id', e.target.value)}>
          <option value="">—</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
      </label>
      <label class="form-label">Waarom? (komt op het dashboard)
        <textarea class="form-input" rows={3} maxLength={1000} value={form.reason} onInput={e => set('reason', e.target.value)} placeholder="Stond elke zaterdag achter de bar en regelde het jeugdtoernooi." />
      </label>
      <AiAssist kind="pride_reason" value={form.reason} onUse={text => set('reason', text)} />
      {canGiveCoins && (
        <label class="form-label">Coin-bonus (optioneel)
          <input class="form-input" type="number" min="0" max="1000" value={form.coins} onInput={e => set('coins', e.target.value)} />
        </label>
      )}
      {error && <div class="alert alert-error">{error}</div>}
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
        <Button onClick={save} disabled={saving || !form.user_id || !form.reason.trim()}>{saving ? 'Opslaan…' : 'Kiezen'}</Button>
      </div>
    </div>
  );
}

function MomentForm({ onSaved, onCancel }) {
  // Today in local time (toISOString would give yesterday shortly after midnight)
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const [form, setForm] = useState({ title: '', body: '', moment_date: today, pinned: false });
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function save() {
    setSaving(true); setError(null);
    const fd = new FormData();
    for (const [k, v] of Object.entries(form)) fd.append(k, String(v));
    if (photo) fd.append('photo', photo);
    try { await api('/api/pride/moments', { method: 'POST', body: fd, headers: {} }); onSaved(); }
    catch (err) { setError(message(err)); }
    setSaving(false);
  }
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <label class="form-label">Wat is er te vieren?
        <input class="form-input" maxLength={150} value={form.title} onInput={e => set('title', e.target.value)} placeholder="JO13-1 kampioen!" />
      </label>
      <label class="form-label">Tekst (optioneel)
        <textarea class="form-input" rows={3} maxLength={4000} value={form.body} onInput={e => set('body', e.target.value)} />
      </label>
      <AiAssist kind="pride_moment" value={form.body} context={{ title: form.title }} onUse={text => set('body', text)} />
      <label class="form-label">Datum
        <input class="form-input" type="date" value={form.moment_date} onInput={e => set('moment_date', e.target.value)} />
      </label>
      <label class="form-label">Foto (optioneel)
        <input class="form-input" type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] || null)} />
      </label>
      <label style="display:flex;gap:8px;align-items:center;font-size:14px">
        <input type="checkbox" checked={form.pinned} onChange={e => set('pinned', e.target.checked)} /> Bovenaan vastzetten
      </label>
      {error && <div class="alert alert-error">{error}</div>}
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
        <Button onClick={save} disabled={saving || !form.title.trim()}>{saving ? 'Opslaan…' : 'Plaatsen'}</Button>
      </div>
    </div>
  );
}

export default function ClubtrotsView() {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const load = () => api('/api/pride').then(setData).catch(err => setError(err.code === 'forbidden' ? 'Je hebt geen toegang tot clubtrots.' : message(err)));
  useEffect(() => { load(); }, []);
  if (error && !data) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  const { current, volunteers, moments, can_manage: canManage, month } = data;
  const taken = new Set(volunteers.map(v => v.month));
  const done = () => { setModal(null); load(); };
  async function run(fn) { setError(null); try { await fn(); load(); } catch (err) { setError(message(err)); } }
  const removeMoment = m => confirm(`"${m.title}" verwijderen?`) && run(() => api(`/api/pride/moments/${m.id}`, { method: 'DELETE' }));
  const removeVolunteer = v => confirm(`Keuze voor ${monthLabel(v.month)} verwijderen? Een gegeven coin-bonus blijft staan.`) && run(() => api(`/api/pride/volunteers/${v.id}`, { method: 'DELETE' }));

  return (
    <div>
      <div class="page-header" style="margin-bottom:16px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap">
        <h1 class="page-title">🏆 Clubtrots</h1>
        {canManage && (
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <Button variant="secondary" onClick={() => setModal('choose')}>Vrijwilliger kiezen</Button>
            <Button onClick={() => setModal('moment')}>+ Trotsmoment</Button>
          </div>
        )}
      </div>
      {error && <div class="alert alert-error mb-3">{error}</div>}
      <div style="display:flex;flex-direction:column;gap:16px">
        <Card title="⭐ Vrijwilliger van de maand">
          {current ? (
            <div style="display:flex;gap:14px;align-items:center;flex-wrap:wrap">
              <Avatar name={current.display_name} url={current.avatar_url} size={64} />
              <div style="flex:1 1 220px;min-width:0">
                <div style="font-size:18px;font-weight:700">{current.display_name || 'Oud-lid'}</div>
                <div class="text-sm text-muted">{monthLabel(current.month)}</div>
                <div style="margin-top:6px;white-space:pre-line">{current.reason}</div>
              </div>
            </div>
          ) : <div class="text-sm text-muted">Nog niemand gekozen.{canManage ? ' Kies de eerste vrijwilliger van de maand.' : ''}</div>}
        </Card>

        <Card title="🎉 Trotsmomenten">
          {moments.length === 0 && <div class="text-sm text-muted">Nog geen trotsmomenten.</div>}
          <div style="display:flex;flex-direction:column;gap:12px">
            {moments.map(m => (
              <div key={m.id} style={`display:flex;gap:12px;flex-wrap:wrap;padding:10px;border:1px solid var(--color-border);border-radius:10px;min-width:0${m.pinned ? ';border-color:var(--color-primary)' : ''}`}>
                {m.has_photo && <img src={`/api/pride/moments/${m.id}/photo`} alt="" style="width:160px;max-width:100%;height:110px;object-fit:cover;border-radius:8px;flex-shrink:0" />}
                <div style="flex:1 1 200px;min-width:0">
                  <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
                    {m.pinned && <span title="Vastgezet" aria-label="Vastgezet">📌</span>}
                    <span style="font-weight:700">{m.title}</span>
                  </div>
                  <div class="text-sm text-muted">{dayLabel(m.moment_date)}</div>
                  {m.body && <div class="text-sm" style="white-space:pre-line;margin-top:4px">{m.body}</div>}
                  {canManage && <div style="margin-top:6px"><Button size="sm" variant="secondary" onClick={() => removeMoment(m)}>Verwijderen</Button></div>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="🙏 Bedankjes">
          <div style="margin-bottom:12px"><ThanksForm onSent={load} /></div>
          {data.thanks.length === 0 && <div class="text-sm text-muted">Nog geen bedankjes. Wie verdient er een?</div>}
          {data.thanks.map(t => <ThanksItem key={t.id} t={t} showTo canDelete={canManage || Number(t.from_user_id) === Number(auth.userId)} onChanged={load} />)}
        </Card>

        {volunteers.length > 0 && (
          <Card title="🏅 Eregalerij">
            <div style="display:flex;flex-direction:column;gap:8px">
              {volunteers.map(v => (
                <div key={v.id} style="display:flex;gap:10px;align-items:center;min-width:0">
                  <Avatar name={v.display_name} url={v.avatar_url} size={36} />
                  <div style="flex:1;min-width:0">
                    <div style="font-weight:600;font-size:14px">{v.display_name || 'Oud-lid'} <span class="text-sm text-muted">· {monthLabel(v.month)}</span></div>
                    <div class="text-sm text-muted" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{v.reason}</div>
                  </div>
                  {canManage && <Button size="sm" variant="secondary" onClick={() => removeVolunteer(v)} aria-label={`Keuze ${monthLabel(v.month)} verwijderen`}>✕</Button>}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <Modal isOpen={modal === 'choose'} title="Vrijwilliger van de maand kiezen" onClose={() => setModal(null)}>
        {modal === 'choose' && <ChooseForm month={month} taken={taken} canGiveCoins={data.can_give_coins} onSaved={done} onCancel={() => setModal(null)} />}
      </Modal>
      <Modal isOpen={modal === 'moment'} title="Nieuw trotsmoment" onClose={() => setModal(null)}>
        {modal === 'moment' && <MomentForm onSaved={done} onCancel={() => setModal(null)} />}
      </Modal>
    </div>
  );
}
